import apiClient from './client';
import { apiError } from './errors';
import type { OrganizationUser, PendingInvite, CreateInviteResponse, ApiResponse } from '../types';

export async function getOrganizationUsers(): Promise<OrganizationUser[]> {
  try {
    const response = await apiClient.get<ApiResponse<OrganizationUser[]>>('/api/v1/users');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to get users');
    }
    return response.data.data;
  } catch (err) {
    throw apiError(err, 'Failed to get users');
  }
}

export async function getPendingInvites(): Promise<PendingInvite[]> {
  try {
    const response = await apiClient.get<ApiResponse<PendingInvite[]>>('/api/v1/invites/organization');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to get invites');
    }
    return response.data.data;
  } catch (err) {
    throw apiError(err, 'Failed to get invites');
  }
}

export interface CreateInviteRequest {
  email: string;
  role: 'admin' | 'billing' | 'tech';
}

export async function createInvite(data: CreateInviteRequest): Promise<CreateInviteResponse> {
  try {
    const response = await apiClient.post<ApiResponse<CreateInviteResponse>>('/api/v1/invites', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create invite');
    }
    return response.data.data;
  } catch (err) {
    throw apiError(err, 'Failed to create invite');
  }
}

export async function revokeInvite(inviteId: string): Promise<void> {
  try {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/v1/invites/${inviteId}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to revoke invite');
    }
  } catch (err) {
    throw apiError(err, 'Failed to revoke invite');
  }
}

export async function resendInvite(inviteId: string): Promise<void> {
  try {
    const response = await apiClient.post<ApiResponse<void>>(`/api/v1/invites/${inviteId}/resend`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to resend invite');
    }
  } catch (err) {
    throw apiError(err, 'Failed to resend invite');
  }
}

export async function removeUser(userId: string, reason?: string): Promise<void> {
  try {
    const trimmed = reason?.trim();
    const response = await apiClient.delete<ApiResponse<unknown>>(
      `/api/v1/users/${userId}`,
      trimmed ? { data: { reason: trimmed } } : undefined
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to remove user');
    }
  } catch (err) {
    throw apiError(err, 'Failed to remove user');
  }
}
